import { useStoryblok, useStoryblokApi } from '@storyblok/vue'
import { toValue } from 'vue'
import { defineStore } from 'pinia'
import {
  DEFAULT_FETCH_HEADERS,
  DEFAULT_DOCUMENT_USE_CASE,
  ERROR_MESSAGES
} from '../utils/constants'
import { useAlert } from '../composable/useAlert'

const { log } = useAlert()

export const useWizcoStore = defineStore('wizco', {
  /*
    STATE (/reactive /data)
  */
  state: () => ({
    steps: [],
    activeStep: null,
    activeStepUuid: '',
    isActiveStepReady: false,
    forwardingUrlObject: null,
    stepData: {}, // Beinhaltet alle eingegebenen Daten des Users durch erfolgreich abgeschlossene Schritte (inkl. Zusatzinfos), die dem Wizard-Flow folgen (nur Daten, für Schritte, die sich in wizardFlowStepUuids befinden)
    settings: null,
    sessionData: null,
    checksum: null,
    isEditMode: false,
    trackingEvents: [],
    trackingToken: '',
    decisionContexts: [],
    authTokenContext: '',
    customerStatus: '',
    documentLinks: {},
    isStepLoading: false,
    isLoading: true,
    alreadySentStepUuids: [], // Liste aller Schritt-UUIDs, die der User in diesem Wizard erfolgreich abgeschlossen hat (beinhaltet alle Schritte, unsortiert)
    wizardFlowStepUuids: [], // Liste der Schritt-UUIDs, die der User in diesem Wizard abgeschlossen hat, die dem Wizard-Flow folgen, in der korrekten Reihenfolge (beinhaltet nur Schritte, die einen gewählten Flow folgen)
    stepDataBackup: {} // Beinhaltet alle eingegebenen Daten des Users
  }),

  /*
    GETTERS (/computed)
  */
  getters: {
    submitBody: (state) => {
      return {
        currentStepUuid: state.activeStepUuid, // UUID des Aktuellen Schrittes, sofern vorhanden
        previousStepUuids: state.alreadySentStepUuids, // Array aus den UUIDs, der vorherigen Schritte
        stepData: state.stepData, // Alle bisher eingegebenen Daten im Objekt, mit jedem Schritt als Objekt mit Key=WizCoStep-Story-UUID - bsp: { 'a-a-a-a': { key: 'a', data: {} }, … }
        wizardUuid: state.settings.uuid, // UUID von der settings-komponente im wizard-ordner
        checksum: state.checksum, // Checksumme aller Steps im Gänze
        sessionData: state.sessionData, // JWE mit den data-vault referenzen
        isEditMode: state.isEditMode, // befindet sich der WizCo aktuell im Testmodus (bspw. im Live-Editor von Storyblok)
        trackingToken: state.trackingToken // der von ET übergebene Wert "p4etoken", der von der dem WizCo aufrufenden Eltern-Komponente übergeben werden muss
      }
    }
  },

  /*
    ACTIONS (/ methods)
  */
  actions: {
    async getSettings(wizardIdentifier) {
      const storylokSettings = {
        version: this.$WIZCO_STORYBLOK_VERSION,
        token: this.$WIZCO_STORYBLOK_TOKEN
      }
      if (!wizardIdentifier.includes('/')) {
        storylokSettings.find_by = 'uuid'
      }
      const storyblokApi = useStoryblokApi()
      const { data } = await storyblokApi.get(`cdn/stories/${wizardIdentifier}`, storylokSettings)
      this.settings = data.story
    },

    async getStep(stepUuid) {
      if (stepUuid === this.activeStepUuid) {
        return
      }
      this.isActiveStepReady = false
      const alreadyAvailableStep = this.steps.find((step) => step.uuid === stepUuid)
      if (alreadyAvailableStep) {
        this.activeStep = alreadyAvailableStep
        this.activeStepUuid = alreadyAvailableStep.uuid
      } else {
        this.isStepLoading = true
        const story = await useStoryblok(stepUuid, {
          version: this.$WIZCO_STORYBLOK_VERSION,
          token: this.$WIZCO_STORYBLOK_TOKEN,
          find_by: 'uuid'
        })
        this.activeStep = story
        this.steps.push(toValue(story))
        this.activeStepUuid = this.activeStep.uuid
        this.isStepLoading = false
      }
      this.stepData[this.activeStepUuid] ??= {
        key: this.activeStep.content.fieldKey ?? null,
        data: this.stepDataBackup[this.activeStepUuid] ?? {},
        checksum: this.checksum
      }
      if (this.wizardFlowStepUuids.at(-1) !== stepUuid) {
        this.wizardFlowStepUuids.push(stepUuid)
      }
    },

    async submit() {
      if (this.isStepLoading) {
        return
      }
      this.isStepLoading = true
      return fetch(new URL('/v2/step', this.$WIZCO_API_DOMAIN), {
        method: 'POST',
        headers: DEFAULT_FETCH_HEADERS,
        body: JSON.stringify(this.submitBody)
      })
        .then(async (response) => {
          const body = await response.json()
          if (!body) {
            log(ERROR_MESSAGES.step)
            throw new Error('could not found json data in body')
          }
          this.sessionData = body.sessionData ?? null
          if (!response.ok) {
            log(ERROR_MESSAGES.step)
            throw new Error('no success')
          }
          this.checksum = body.checksum
          if (body.trackingEvents.length > 0) {
            this.trackingEvents = body.trackingEvents
          }
          if (body.nextStepExternal && body.isNextStepExternal === true) {
            this.isActiveStepReady = false
            this.forwardingUrlObject = body.nextStepExternal
            return
          }
          if (this.alreadySentStepUuids.indexOf(this.activeStepUuid) === -1) {
            this.alreadySentStepUuids.push(this.activeStepUuid)
          }
          this.getStep(body.nextStepUuid)
        })
        .catch(() => {
          log(ERROR_MESSAGES.step)
        })
        .finally(() => {
          this.isStepLoading = false
        })
    },

    start(sessionData) {
      this.isLoading = true
      fetch(new URL('/v2/start', this.$WIZCO_API_DOMAIN), {
        method: 'POST',
        headers: DEFAULT_FETCH_HEADERS,
        body: JSON.stringify({
          currentStepUuid: this.activeStepUuid,
          wizardUuid: this.settings.uuid,
          trackingToken: this.trackingToken,
          sessionData
        })
      })
        .then(async (response) => {
          const body = await response.json()
          if (!body || !response.ok) {
            log(ERROR_MESSAGES.start)
            return
          }
          this.sessionData = body.sessionData
          this.stepData = Object.assign(body.stepData, this.stepData)
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    async getDocuments(useCase = DEFAULT_DOCUMENT_USE_CASE) {
      return fetch(new URL(`/v1/document/${useCase}`, this.$WIZCO_API_DOMAIN), {
        method: 'POST',
        headers: DEFAULT_FETCH_HEADERS,
        body: JSON.stringify(this.submitBody)
      })
        .then(async (response) => {
          const body = await response.json()
          if (!body || !response.ok) {
            log(ERROR_MESSAGES.document)
            return
          }
          this.documentLinks = body.documentLinks
        })
        .catch(() => {
          log(ERROR_MESSAGES.document)
        })
    },

    stepBack() {
      const indexOfLastStep = this.wizardFlowStepUuids.indexOf(this.activeStepUuid) - 1
      if (indexOfLastStep < 0 && window) {
        history.back()
        return
      }
      const uuidOfLastStep = this.wizardFlowStepUuids[indexOfLastStep]
      this.checksum = this.stepData[uuidOfLastStep].checksum // set checksum to the state of the last step
      this.stepDataBackup[this.activeStepUuid] = this.stepData[this.activeStepUuid].data // store data state of current step in case if the step is called again
      delete this.stepData[this.activeStepUuid] // delete stepData of actual step to let the data be in sync with checksum of last step
      this.wizardFlowStepUuids.pop() // remove last uuid to prevent loops
      this.getStep(uuidOfLastStep)
    }
  }
})
